import { tKey, type I18nKey } from "@/core/i18n";
import {
  getHyperparamRegistryEntry,
  ModelBackendEnum,
  ProjectTypeEnum,
} from "@repo/schema";

/**
 * Helpers for reading and patching individual keys inside the free-form
 * `customHyperparams` JSON string.
 *
 * The blob is the single source of truth: presets write it, the AI suggestion
 * rewrites it, and the hyperparams editor edits it directly. Any dedicated
 * control for one key (today: model size) therefore derives its value from the
 * blob and patches back into it, rather than holding parallel state that would
 * silently disagree the moment a preset is applied.
 */

/** Returns null when the string isn't a JSON object — controls disable on null. */
export const parseHyperparamsObject = (
  value: string,
): Record<string, unknown> | null => {
  if (!value.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

/** Set one key in the blob, preserving the rest and the 2-space formatting. */
export const patchHyperparam = (
  value: string,
  key: string,
  next: unknown,
): string | null => {
  const parsed = parseHyperparamsObject(value);
  if (!parsed) return null;
  return JSON.stringify({ ...parsed, [key]: next }, null, 2);
};

export type ModelSize = "nano" | "small" | "medium" | "large";

/** Sizes offered in the UI, smallest first. */
export const SELECTABLE_MODEL_SIZES: ModelSize[] = [
  "nano",
  "small",
  "medium",
  "large",
];

export const MODEL_SIZE_LABELS = {
  nano: tKey("model:modelSize.labels.nano"),
  small: tKey("model:modelSize.labels.small"),
  medium: tKey("model:modelSize.labels.medium"),
  large: tKey("model:modelSize.labels.large"),
} satisfies Record<ModelSize, I18nKey<"model">>;

export const MODEL_SIZE_HINTS = {
  nano: tKey("model:modelSize.hints.nano"),
  small: tKey("model:modelSize.hints.small"),
  medium: tKey("model:modelSize.hints.medium"),
  large: tKey("model:modelSize.hints.large"),
} satisfies Record<ModelSize, I18nKey<"model">>;

/**
 * The trainer's native `size` letter behind each pill, per model type. The two
 * model families name their capacities differently, so the pills (which are
 * what users see) map onto a different letter for each.
 */
const NATIVE_SIZES: Partial<Record<ProjectTypeEnum, Record<ModelSize, string>>> = {
  [ProjectTypeEnum.DETECTION]: { nano: "t", small: "s", medium: "m", large: "c" },
  [ProjectTypeEnum.SEGMENTATION]: { nano: "n", small: "s", medium: "m", large: "l" },
};

export const toNativeSize = (
  trainingType: ProjectTypeEnum,
  size: ModelSize,
): string | null => NATIVE_SIZES[trainingType]?.[size] ?? null;

/** Current size pill from the blob's native `size`, or null when unset/foreign. */
export const readModelSize = (
  trainingType: ProjectTypeEnum,
  params: Record<string, unknown> | null,
): ModelSize | null => {
  const native = params?.size;
  const table = NATIVE_SIZES[trainingType];
  if (typeof native !== "string" || !table) return null;
  return (
    SELECTABLE_MODEL_SIZES.find((size) => table[size] === native) ?? null
  );
};

/**
 * Layer freezing, expressed as `freeze`: `"backbone"` holds the pretrained
 * feature extractor fixed so only the later layers train, `"none"` trains
 * everything.
 *
 * Tri-state on purpose. The training service applies its own small-dataset
 * default when the key is absent, so a plain on/off switch would show "off"
 * while the run actually froze the backbone. "Auto" names that absence
 * honestly; On/Off write a value and take the decision away from the service.
 */
export type FreezeMode = "auto" | "on" | "off";

export const FREEZE_MODES: FreezeMode[] = ["auto", "on", "off"];

export const FREEZE_MODE_LABELS = {
  auto: tKey("model:freezeMode.auto"),
  on: tKey("model:freezeMode.on"),
  off: tKey("model:freezeMode.off"),
} satisfies Record<FreezeMode, I18nKey<"model">>;

export const readFreezeMode = (
  params: Record<string, unknown> | null,
): FreezeMode => {
  if (!params || !("freeze" in params)) return "auto";
  const freeze = params.freeze;
  return freeze === "none" || freeze === false || freeze === 0 ? "off" : "on";
};

/** Returns the patched blob, or null when it isn't parseable JSON. */
export const writeFreezeMode = (
  value: string,
  mode: FreezeMode,
): string | null => {
  const parsed = parseHyperparamsObject(value);
  if (!parsed) return null;
  if (mode === "auto") {
    // Absence is the signal — deleting the key hands the decision back.
    const { freeze: _omitted, ...rest } = parsed;
    return JSON.stringify(rest, null, 2);
  }
  return JSON.stringify(
    { ...parsed, freeze: mode === "on" ? "backbone" : "none" },
    null,
    2,
  );
};

/**
 * Carry a hyperparams blob over when the model type changes. The two families
 * take different native keys, so keys the new type doesn't define are dropped
 * (the trainer would ignore them anyway) and the size pill is re-expressed in
 * the new family's letters. Keys neither registry knows are kept: they are
 * hand-written and pass through to the trainer as typed.
 */
export const retargetHyperparams = (
  value: string,
  from: ProjectTypeEnum,
  to: ProjectTypeEnum,
  backend: ModelBackendEnum,
): string => {
  const parsed = parseHyperparamsObject(value);
  if (!parsed || from === to) return value;

  const fromKeys = getHyperparamRegistryEntry(backend, from)?.params ?? {};
  const toKeys = getHyperparamRegistryEntry(backend, to)?.params ?? {};
  const size = readModelSize(from, parsed);

  const next: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(parsed)) {
    if (key === "size") continue;
    if (key in fromKeys && !(key in toKeys)) continue;
    next[key] = entry;
  }
  const native = size ? toNativeSize(to, size) : null;
  if (native) next.size = native;
  return JSON.stringify(next, null, 2);
};
